<template>
    <rothco-table
        v-model:checked-rows="checkedRows"
        :row-is-checkable="hasInventory"
        key="table"
        v-model:focused="focused"
        v-model:content="gridItems"
        :visible-fields="visibleFields"
        primary-key="item_variation_id"
        :useVirtualRows="true"
        :emptyRowsMessage="props.emptyRowsMessage"
        focusable
        :pt="{
            root: 'b2b-quick-order__table-content'
        }"
    >
        <template v-for="(item, index) in gridItems" :key="`item_no_${index}`" v-slot:[`item_no_${index}`]="slotProps">
            <a @click.prevent="openProductImageModal(item.data.primary_image)" href="#" title="View Product Image"
                :class="`${(slotProps.header.show_only == 'mobile') ? 'card-title' : 'table-name'}`">
               {{ item.data.item_no }}
            </a>
        </template>

        <template v-for="(item, index) in gridItems" :key="`item_name_${index}`" v-slot:[`item_name_${index}`]="slotProps">
            <Link v-if="slotProps.header.show_only == 'mobile'" class="card-title" :href="route('productDetail.show', [item.data.item.seo_name])">
                {{ item.data.item_name }}
            </Link>
            <a target="_blank" v-else class="table-name" :href="route('productDetail.show', [item.data.item.seo_name])">
                {{ item.data.item_name }}
            </a>
            <RemoveIcon v-if="slotProps.header.show_only == 'mobile'" class="remove-icon mobile-only" :alt="`Remove product ${item.data.item.item_name}`" aria-label="Remove product" :id="`mobile_delete_list_item_${index}`"
                @click="openItemDelete(item.data.list_item_id)" />
        </template>

        <template v-for="(item, index) in gridItems" :key="`upc_${index}`" v-slot:[`upc_${index}`]="slotProps">
            <a class="table-name" :tabindex="0" @click="showUpcBarCode(item.data.upc)" :id="`upcViewer-${index}`" >
                {{ item.data.upc }}
            </a>
        </template>

        <template v-for="(item, index) in gridItems" :key="`variant_${index}`" v-slot:[`variant_${index}`]="slotProps">
            <div class="badge-wrapper">
                <p v-if="Object.keys(item?.data?.description ?? {}).length === 0" class="paragraph-default badge" style="visibility: hidden">No Variants</p>
                <p v-for="(variantValue,i) in (item.data.description ?? {})" :key="i" class="paragraph-default badge">{{ variantValue }}</p>
            </div>
        </template>
   
        <template v-for="(item, index) in gridItems" :key="`quantity_${index}`" v-slot:[`quantity_${index}`]="slotProps">
            <input
                :id="`desktop_quantity_input_${index}`" :key="`desktop_quantity_input_${index}`"
                v-model="gridItems[index].data.quantity" @input="recalculatePricing(index);recalculateTotals(gridItems)" @blur="(e)=>updateListItemQuantity(index)"
                :class="{'table-input mob-text-left desk-text-center': true, 'invalid-state-input': !validateQuantity(item.data.quantity)}"
                :disabled="!hasInventory(item.data)" 
                type="number" min="0" step="1" max="1000000" autocomplete="off" :aria-label="`Quantity for ${item.data.item.item_name}`"
            >
        </template>

        <template v-for="(item, index) in gridItems" :key="`inventory_level_${index}`" v-slot:[`inventory_level_${index}`]="slotProps">
            <span v-if="slotProps.header.show_only == 'mobile'">
                <p class="paragraph-caption">{{ item.data.inventory_level ? item.data.inventory_level : '---' }}</p>
            </span>
            <span v-else>
                <a v-if="!hasInventory(item.data) && hasETADate(item.data)" href="#" class="get-notified" @click="showReceiveEmailModal(item.data.item_variation_id)">Get Notified</a>
                <p v-if="!hasInventory(item.data)" class="paragraph-caption">
                    {{ hasETADate(item.data) && isClient ? `ETA ${moment(item.data.eta_date).format('MM/DD/YY')}` : '---' }}
                </p>
                <p v-else class="paragraph-default">
                    {{ item.data.inventory_level }}
                </p>
            </span>
        </template>

        <template v-for="(item, index) in gridItems" :key="`price_${index}`" v-slot:[`price_${index}`]="slotProps">
            <p class="paragraph-default desk-text-right grid-price">
                <span :class="{'loading-price' : isLoadingPrice && !item.data.current_unit_price.price}">
                    {{ !canSeePricing ? "---" : (item.data.current_unit_price.price) ? (formatPriceAsString(+item.data.current_unit_price.price)) : (isLoadingPrice ? '' : "---") }}
                </span>
                <s class="grid-origin-price" v-if="canSeePricing && item.data.wholesale_price && (+item.data.current_unit_price.price) < (+item.data.wholesale_price)
                ">Orig: {{ (item.data.wholesale_price) ? (formatPriceAsString(+item.data.wholesale_price)) : "---" }}</s>
            </p>
        </template>

         <template v-for="(item, index) in gridItems" :key="`mobile_price_${index}`" v-slot:[`mobile_price_${index}`]="slotProps">
            <p class="paragraph-default desk-text-right grid-price">
                <span>{{ !canSeePricing ? "---" : (item.data.current_unit_price.price) ? (formatPriceAsString(+item.data.current_unit_price.price)) : "---" }}</span>
                <s class="grid-origin-price" v-if="canSeePricing && item.data.wholesale_price && (+item.data.current_unit_price.price) < (+item.data.wholesale_price)
                ">Orig: {{ (item.data.wholesale_price) ? (formatPriceAsString(+item.data.wholesale_price)) : "---" }}</s>
            </p>
        </template>

        <template v-for="(item, index) in gridItems" :key="`case_pack_${index}`" v-slot:[`case_pack_${index}`]="slotProps">
            <p class="paragraph-default desk-text-right">
                {{ item.data?.next_case?.min_order_units ? item.data.next_case?.min_order_units : "" }}
            </p>
        </template>

        <template v-for="(item, index) in gridItems" :key="`case_price_${index}`" v-slot:[`case_price_${index}`]="slotProps">
            <p class="paragraph-default desk-text-right">
               {{ !canSeePricing ? "---" : item?.data.next_case?.min_order_units != "N/A" ? `${formatPriceAsString(+item.data.next_case.price)}` : "N/A" }}
            </p>
        </template>

        <template v-for="(item, index) in gridItems" :key="`cost_${index}`" v-slot:[`cost_${index}`]="slotProps">
            <p class="paragraph-default desk-text-right">
                {{ !canSeePricing ? "---" : (item.data.quantity && item.data.current_unit_price.price) ? ('$' + formatTotalPrice(item.data.current_unit_price.price, item.data.quantity)) : "---" }}
            </p>
        </template>

        <template v-for="(item, index) in gridItems" :key="`delete_${index}`" v-slot:[`delete_${index}`]="slotProps">
            <div class="flex-row justify-center" style="width:100%;">
                <RemoveIcon class="remove-icon" :alt="`Remove product ${item.data.item.item_name}`" aria-label="Remove product" :id="`desktop_delete_list_item_${index}`"
                @click="openItemDelete(item.data.list_item_id)" />
            </div>
        </template>

        <template v-for="(item, index) in gridItems" :key="index" v-slot:[`stock_info_mobile_${index}`]="slotProps">
            <div>
                <p class="out-of-stock-label">{{item.data.item.is_coming_Soon == 1 ? "Coming Soon" : "Out of Stock"}}</p> 
                    <!--TODO: logic to differentiate between 'out of stock' and 'coming soon' -->
                <p class="paragraph-details">ETA {{ isClient ? moment(item.data.eta_date).format('MM/DD/YY') : '---' }}</p>
            </div>
            <a href="#" class="get-notified" @click="showReceiveEmailModal(item.data.item_variation_id)">Get Notified</a>
        </template>
    </rothco-table>
</template>
<script lang="ts" setup>
    import moment from "moment";
    import RothcoTable from "@/components/RothcoTable.vue";
    import { useDialog } from "primevue/usedialog";
    import { type ItemVariation } from "@interfaces/ProductInterfaces";
    import { computed, ref, watch, onMounted, nextTick, inject } from "vue";
    import { useOrderingItemsGrid } from "@/helpers/composables/useOrderingItemsGrid";
    import { useMagicKeys, useActiveElement } from "@vueuse/core";
    import ReceiveEmailModal from "@/components/ReceiveEmailModal.vue";
    import ListDeleteForm from "@/components/ListDeleteModal.vue";
    import UpcBarcodeModal from "@/components/UpcBarcodeModal.vue";
    import ImagePreviewModal from "@/components/ImagePreviewModal.vue";
    import { router, Link } from "@inertiajs/vue3";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { formatTotalPrice, formatPriceAsString } from "@/helpers/composables/usePriceFormatting";
    import RemoveIcon from "@icons/remove-icon.svg";
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages"
    import { productUploadImageUrl } from "@/helpers/productUploadImageUrl";
    
    let route = inject("route");
    let props = defineProps({
        products: {
            type: Array as PropType<ItemVariation[]>,
            required: true,
            default: () => []
        },
        listId: {
            type: Number,
            required: true
        },
        addingItems: {
            type: Boolean,
            default: false
        },
        emptyRowsMessage: {
            type: String,
            default: "No data available"
        }
    });

    let store = useSettingsStore();
    let { canSeePricing } = storeToRefs(store);

    function toSnakeCase(str) {
        return str.toLowerCase().replace(/\s+/g, "_");
    }
    let productSpecs = computed(() => {
        let specs = [];
        if (props.products.length) {
            let allSpecs = props.products.map(item => Object.keys(item?.description ?? {}));
            let uniqueSpecs = Array.from(new Set(allSpecs.flat()));
            for (let spec of uniqueSpecs) {
                specs.push({
                    label: spec,
                    field: toSnakeCase(spec),
                    is_custom: false,
                    show_only: 'desktop',
                    pt: {
                        headerCell: { class: 'title-xs text-left' },
                        bodyCell: { class: 'mob-w-25 text-left', 'data-label': "Item" },
                    }
                });
            }
        }
        return specs;
    });
    
    let visibleFields = computed(() => {
        return [        
            {
                label: "Item Name",
                field: "item_name",
                is_custom: true,
                show_only: 'mobile',
                pt: {
                    bodyCell: { class: 'mob-w-100 text-left', 'data-label': "Item" }
                }
            },
            {
                label: "Item #",
                field: "item_no",
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'mob-w-25 text-left', 'data-label': "Item" },
                }
            },
            {
                label: "Variant",
                field: "variant",
                is_custom: true,
                show_only: 'mobile',
                pt: {
                    bodyCell: { class: 'mob-w-75', 'data-label': "Variation" }
                }
            },
            {
                label: "Item Name",
                field: "item_name",
                is_custom: true,
                show_only: "desktop",
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'text-left', 'data-label': "Item" }
                }
            },
            ...productSpecs?.value,
            {
                label: "UPC",
                field: "upc",
                is_custom: true,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'text-left' },
                }
            },
            {
                label: "Wgt. (lbs)",
                field: "formattedWeight",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'text-left' },
                    bodyCellContent: { class: 'paragraph-default text-center'}
                }
            },
            {
                label: "MSRP",
                field: "formattedMsrp",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCellContent: { class: 'paragraph-default text-right'}
                }
            },
            {
                label: "MAP",
                field: "formattedMap",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCellContent: { class: 'paragraph-default text-right'}
                }
            },
            {
                label: "Price",
                field: "price",
                is_custom: true,
                show_only: "desktop",
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCell: { class: 'mob-w-25', 'data-label': "Wholesale"},
                    bodyCellContent: { class: 'paragraph-default mob-text-left desk-text-right'}
                }
            },
            {
                label: "UOM",
                field: "uom",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCellContent: { class: 'paragraph-default text-center'}
                }
            },
            {
                label: "Case Pack",
                field: "case_pack",
                is_custom: true,
                show_only: 'desktop',
                visible: hasCasePricing.value,
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: { class: 'text-left' }
                }
            },
            {
                label: "Case Price",
                field: "case_price",
                is_custom: true,
                show_only: 'desktop',
                visible: hasCasePricing.value,
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCell: { class: 'paragraph-default text-right' }
                }
            },
            {
                label: "Avail.",
                field: "inventory_level",
                is_custom: true,
                show_only: 'mobile',
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: (state)=>{
                        return {
                            class: 'mob-w-25 mobile-only', 
                            'data-label': "Available" ,
                            colspan: 1
                        }
                    }
                }
            },
            {
                label: "Avail.",
                field: "inventory_level",
                is_custom: true,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: (state)=>{
                        return {
                            class: 'mob-w-25 desktop-only', 
                            'data-label': "Available" ,
                            colspan: +state?.instance?.rowData?.data?.inventory_level == 0 ? 2 : 1
                        }
                    }
                }
            },
            {
                label: "Price",
                field: "mobile_price",
                is_custom: true,
                show_only: "mobile",
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCell: { class: 'mob-w-25', 'data-label': "Price"},
                    bodyCellContent: { class: 'paragraph-default mob-text-left desk-text-right'}
                }
            },
            {
                label: "Cost",
                field: "cost",
                is_custom: true,
                show_only: "mobile",
                pt: {
                    headerCell: { class: 'title-xs text-right'},
                    bodyCell: { class: 'text-right', 'data-label': "Cost"}
                }
            },
            {
                label: "Qty",
                field: "quantity",
                is_custom: true,
                show_only: "desktop",
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: (state)=>{
                        return {
                            class: ['qty-cell', (+state?.instance?.rowData?.data?.inventory_level == 0 ? 'hide-column' : 'desktop-only')], 
                            'data-label': "Qty" ,
                            colspan: +state?.instance?.rowData?.data?.inventory_level == 0 ? 0 : 1,
                            style: +state?.instance?.rowData?.data?.inventory_level == 0 ? 'display:none;' : ''
                        }
                    }
                }
            },
            {
                label: "Qty",
                field: "quantity",
                is_custom: true,
                show_only: "mobile",
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'mob-w-25 qty-cell mob-text-left', 'data-label': "Qty"}
                }
            },
            {
                label: "Cost",
                field: "cost",
                is_custom: true,
                show_only: "desktop",
                pt: {
                    headerCell: { class: 'desktop-only title-xs text-right' },
                    bodyCell: { class: 'desktop-only text-right' }
                }
            },
            {
                label: "Delete",
                field: "delete",
                is_custom: true,
                show_only: "desktop",
                visible: !props.addingItems,
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                }
            },
            {
                label: "Stock Info Mobile",
                field: "stock_info_mobile",
                is_custom: true,
                pt: {
                    headerCell: { class: 'mobile-only title-xs text-center' },
                    bodyCell: (state)=>{
                        return {
                            class: ['table-item--out-of-stock mob-w-100 mobile-only'],
                            style: ((+state?.instance?.rowData?.data?.inventory_level != 0 || !state?.instance?.rowData?.data?.eta_date) && 'display:none;')
                        }
                    }
                }
            }
        ].filter((field) => field.visible !== false);
    });

    let isClient = computed(()=>{
        return typeof window !== 'undefined'
    })

    const hasInventory = (row)=>{
        return +row.inventory_level > 0 || row.inventory_level === '99+'
    }

    const hasETADate = (row) => {
        return row.eta_date ?? false;
    }

    let availableRows = computed(() => {
        return gridItems.map((item,index)=>{
            return {...item, grid_item_index: index}
        }).filter(
            (item) =>
                item.data.inventory_level > 0 ||
                item.data.inventory_level == "99+"
        );
    });

    let formatters = {
        ...productSpecs.value.reduce((acc, spec) => {
            acc[spec.field] = (item) =>
                item.description[spec.label];
            return acc;
        }, {}),
        variant: (item) =>
            item.description ? Object.values(item.description).join(" - ") : "",
        formattedMap: (item) => (item.map ? `${formatPriceAsString(+item.map)}` : ""),
        formattedMsrp: (item) =>
            item.msrp ? `${formatPriceAsString(+item.msrp)}` : "",
        formattedWeight: (item) =>
            item.weight ? `${(+item.weight).toFixed(2)}` : "",
        formattedPrice: (item) =>
            item.current_unit_price?.price && canSeePricing.value
                ? `${formatPriceAsString(+item.current_unit_price.price)}`
                : "---",
        formattedWholesalePrice: (item) =>
            item.wholesale_price && canSeePricing.value
                ? `${formatPriceAsString(+item.wholesale_price)}`
                : "---",
        formattedCasePack: (item) =>
            item?.next_case?.min_order_units
                ? item.next_case?.min_order_units
                : "",
        formattedCasePrice: (item) =>
            !canSeePricing.value
                ? "---"
                : item?.next_case?.min_order_units != "N/A"
                  ? `${formatPriceAsString(+item.next_case.price)}`
                  : "N/A"
    };
    let {
        gridItems,
        recalculatePricing,
        refetchPricing,
        recalculateTotals,
        totalPrice,
        totalQuantity,
        totalSkusToAdd,
        totalStylesToAdd,
        hasCasePricing,
        validateQuantity,
        addToList,
        updateListItem,
        getUpcPricing,
    } = useOrderingItemsGrid({
        items: props.products.map(item => ({
            ...item,
            product_line_code: item.item.product_line_code,
        })),
        formatters: new Map(Object.entries(formatters)),
        source: import.meta.env.VITE_QUICK_ORDERING_GRID_SOURCE
    });

    let focused = ref(false);
    let { shift, enter } = useMagicKeys();
    let activeElement = useActiveElement();

    watch(enter, (v) => {
        if (v) {
            if (focused.value) {
                const currentId = activeElement.value?.id;
                const currIdIndex = currentId?.split("_").pop();
                const currAvIndex = availableRows.value.findIndex((item)=>(currIdIndex == item.grid_item_index));
                if (shift.value && currIdIndex != null && currAvIndex > 0) {
                    const prev = availableRows.value[currAvIndex-1].grid_item_index;
                    const el = document.getElementById(
                        `desktop_quantity_input_${prev}`
                    );
                    el?.focus?.();
                } else if (!shift.value && currIdIndex != null) {
                    if(props.addingItems && ((currAvIndex == availableRows.value.length - 1) || [0,'0'].includes(gridItems[currIdIndex].data.quantity))){
                        add();
                    }else if(currAvIndex < availableRows.value.length - 1){
                        const next = availableRows.value[currAvIndex+1].grid_item_index;
                        const el = document.getElementById(
                            `desktop_quantity_input_${next}`
                        );
                        el?.focus?.();
                    }
                }
            }
        } else if (activeElement?.value?.id.includes("upcViewer")) {
            let index = activeElement?.value?.id.split("-").pop();
            let upc = gridItems[index].data.upc;
            showUpcBarCode(upc);
        }
    });

    let itemVariationId = ref(0);
    let showModal = ref(false);

    let dialog = useDialog();

    let showReceiveEmailModal = (newVariation: number) => {
        itemVariationId.value = newVariation;
        dialog.open(ReceiveEmailModal, {
            props: {
                modal: true,
                showHeader: false
            },
            data: {
                itemVariationId
            }
        });
    };

    const listItemId = ref(null);
    let deleteItemBeforeClose = (id) => {
        gridItems = gridItems.filter((item) => item.data.list_item_id != id);
        recalculateTotals(gridItems);
        router.visit(route("index.quickOrdering"), {
            only: ["current_list", "currentListItems"],
            preserveScroll: true, preserveState: true
        });
    };
    const openItemDelete = (id)=>{
        listItemId.value = id;
        dialog.open(ListDeleteForm, {
            props: {
                modal: true,
                header: "Delete List Item",
            },
            data: {
                type: "item",
                list_id: props.listId,
                list_item_id: listItemId,
                beforeCloseForItem: deleteItemBeforeClose
            },
            onClose: (props) => {
                if(props?.data?.cancelled){
                    let index = gridItems.findIndex((item)=>(item.data.list_item_id == id));
                    if(index > -1){
                        gridItems[index].data.quantity = gridItems[index].data.original_quantity;
                    }
                }
            }
        });
    }
    
    let upcBarcode = ref(false);
    let selectedUpc = ref(null);
    const isLoadingPrice = ref(false);

    const showUpcBarCode = (upc)=>{
        selectedUpc.value = upc;
        dialog.open(UpcBarcodeModal, {
            props: {
                modal: true,
                showHeader: false
            },
            data: {
                upc
            }
        });
    }

    function add() {
        addToList(props.listId, {
            afterFetch: () => {
                router.visit(route("index.quickOrdering"),{
                    only: ["current_list", "currentListItems", "searchParam", 'lists',"last_added_item"]
                });
            }
        });
    }

    function updateListItemQuantity(index, pricerefetch=false) {
        let gridItem = gridItems[index];
        if(!validateQuantity(gridItem.data.quantity)) return
        if(!props.addingItems && !pricerefetch && gridItem.data.quantity == 0) {
            return openItemDelete(gridItem.data.list_item_id);
        }
        if (gridItem.data.list_item_id && ( pricerefetch || gridItem.data.original_quantity != gridItem.data.quantity)) {   
            const payload = {
                source: gridItem.data.source,
                price: gridItem.data.current_unit_price.price,
                quantity: gridItem.data.quantity,
                list_id: props.listId,
                list_item_id: gridItem.data.list_item_id
            };
            updateListItem(payload, {
                afterFetch: (ctx) => {
                    let responseData = JSON.parse(ctx.data);
                    router.visit(route("index.quickOrdering"), {
                        only: ["current_list", 'lists'], preserveScroll: true, preserveState: true
                    });

                    gridItems[index].data = {
                        ...gridItems[index].data,
                        quantity: responseData.quantity,
                        original_quantity: responseData.quantity,
                        current_unit_price: { price: responseData.price },
                        next_case: {
                            min_order_units: responseData.case_pack ?? 'N/A',
                            price: responseData.case_price
                        }
                    }

                    recalculateTotals(gridItems);
                }
            });
        }
    }

    let allTotals = defineModel("allTotals");

    let allAddingLinesQuantity = defineModel("allAddingLinesQuantity", {
        type: Number,
        default: 0
    });
    let checkedRows = ref([]);
    let allTotalsComputed = computed(() => {
        let totalGridSkus =  new Set(gridItems.map(item => item.data.item_variation_id));
        let totalGridStyles =  new Set(gridItems.map(item => item.data.item_no));
        return {
            price: totalPrice.value,
            quantity: totalQuantity.value,
            skus: props.addingItems ? totalSkusToAdd.value : totalGridSkus.size,
            styles: props.addingItems ? totalStylesToAdd.value :totalGridStyles.size
        };
    });

    onMounted(async ()=>{
        if(props.addingItems){
            await nextTick(()=>{
                let firstAvailRow = availableRows.value[0];
                if(!["",null,undefined].includes(firstAvailRow?.grid_item_index)){
                    const el = document.getElementById(`desktop_quantity_input_${firstAvailRow.grid_item_index}`);
                    el?.focus();
                }
            })

            fetchMissingPrice();
        }
    })

    const fetchMissingPrice = () => {
        isLoadingPrice.value = true;
        const upcs = gridItems
            .filter((item) => item.data.current_unit_price?.price === undefined)
            .map((item) => item.data.upc)
            .filter((upc) => upc !== null && upc !== undefined);

        if (!upcs.length) {
            return;
        }

        const CHUNK_SIZE = 50;
        const chunkArray = (array, size) => {
            const chunks = [];
            for (let i = 0; i < array.length; i += size) {
                chunks.push(array.slice(i, i + size));
            }
            return chunks;
        };

        const upcChunks = chunkArray(upcs, CHUNK_SIZE);

        const fetchNextChunk = (chunkIndex = 0) => {
            if (chunkIndex >= upcChunks.length) {
                isLoadingPrice.value = false;
                console.log('fetching price finished');
                return;
            }

            const chunk = upcChunks[chunkIndex];
            getUpcPricing(chunk, {
                afterFetch: (ctx) => {
                    fetchNextChunk(chunkIndex + 1);
                }
            });
        };

        fetchNextChunk();
    };


    watch(
        allTotalsComputed,
        (newVal) => {
            allTotals.value = newVal;
        },
        { immediate: true }
    );

    defineExpose({
        add
    })

    function blurOnWheel(event) {
        event.target.blur();
    }

    const openProductImageModal = (image)=>{
        if(image){
            const imageUrl = image
                ? productUploadImageUrl(image, null)
                : comingSoonImages().small
            dialog?.open(ImagePreviewModal,{
                props: {
                    showHeader: false,
                    modal: true,
                },
                data: {
                    image_url: imageUrl
                }
            });
        }else{
            store.setNotificationMessage({
                message: "Image preview unavailable for this product.",
                type: "error"
            })
        }
    }

    watch(allAddingLinesQuantity, (newVal) => {
        if(checkedRows.value.length == 0){
            checkedRows.value = gridItems.filter((item) => hasInventory(item.data))
        }

        checkedRows.value.forEach((item) => {
            let index = gridItems.findIndex((gridItem) => gridItem.data.item_variation_id == item.data.item_variation_id);
            if (index > -1) {
                gridItems[index].data.quantity = newVal > 0 ? newVal : 0;
                recalculatePricing(index);
            }
        })
        recalculateTotals(gridItems);


    });

    watch(checkedRows, (newVal, oldVal) => {
        for(let index = 0; index < gridItems.length; index++){
            let item = gridItems[index];
            let isNewChecked = newVal.find((checkedItem) => checkedItem.data.item_variation_id == item.data.item_variation_id);
            let isOldChecked = oldVal.find((checkedItem) => checkedItem.data.item_variation_id == item.data.item_variation_id);
            if(isNewChecked && !isOldChecked){
                item.data.quantity = allAddingLinesQuantity.value > 0 ? allAddingLinesQuantity.value : 0;
            }else if(!isNewChecked && isOldChecked){
                item.data.quantity = 0;
            }
            recalculatePricing(index);
        }

        recalculateTotals(gridItems);

});
</script>

<style scoped>
    .invalid {
        outline: 2px solid red;
    }
</style>
